import { type ICompanyCardProps } from "@business-finland/wif-ui-lib/dist/CompanyList";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useCallback, useMemo } from "react";

import { ONE_MIN_IN_MS } from "../../../constants/durations";
import useQuery from "../../../hooks/useQuery";
import type { WifCompany } from "../../../types/wif-companies";

export default function useCompaniesQuery(
  service: string | undefined,
  page = 1,
  limit = 6,
  initCompanyResults?: Wif.Mgnl.Results<WifCompany>
): {
  companyCards?: ICompanyCardProps[];
  totalCount: number;
  pageCount: number;
} {
  const { locale, isPreview } = useRouter();
  const transformer = useTransformWifCompanyToCompanyCardProps();
  const offset = (page - 1) * limit;

  const searchParams = new URLSearchParams();
  searchParams.append("offset", offset.toString());
  searchParams.append("limit", limit.toString());
  service && searchParams.append("service", service);
  locale && searchParams.append("lang", locale);

  const { data, isLoading } = useQuery<Wif.Mgnl.Results<WifCompany>>(
    `/api/companies/?${searchParams.toString()}`,
    {
      cacheDurationInMs: isPreview ? 0 : ONE_MIN_IN_MS * 10,
      initialData: initCompanyResults,
    }
  );

  const totalCount = data?.total || 0;
  const pageCount = Math.ceil(totalCount / limit);
  const companyCards = useMemo(
    () => (isLoading ? undefined : data?.results.map(transformer)),
    [data?.results, isLoading, transformer]
  );

  return { companyCards, totalCount, pageCount };
}

function useTransformWifCompanyToCompanyCardProps(): (
  company: WifCompany
) => ICompanyCardProps {
  const { t } = useTranslation();

  return useCallback(
    (company) => ({
      name: company.name,
      description: company.description || "",
      linkUrl: company.website || "",
      imageUrl: company.logoUrl,
      services: company.services.map(
        (service) => service.label?.toString() || service.value
      ),
      i18n: {
        goToCompanySiteLabel: t("company.goToCompanySite"),
        showLessLabel: t("company.showLess"),
        showMoreLabel: t("company.showMore"),
      },
    }),
    [t]
  );
}
